import { Input } from "@/components/ui/input.tsx";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select.tsx";
import { countryList } from "@/shared/countryList.ts";
import { useState } from "react";

type CountrySelectorProps = {
  value: string; // This will be the country name
  onValueChange: (value: string) => void; // This will pass the country name
  className?: string;
};

/**
 * The country a phone number is being typed in, chosen from a searchable list.
 *
 * Paired with a number field rather than used alone: the country decides how
 * the digits are grouped and what they mean, so the two share one border and
 * the caller reformats what is typed whenever this changes.
 */
export const CountrySelector = ({ value, onValueChange, className }: CountrySelectorProps) => {
  const [searchTerm, setSearchTerm] = useState("");

  // Find the selected country by name
  const selectedCountry = countryList.find((country) => country.name === value) || countryList[0];

  // Filter countries based on search term
  const filteredCountries = countryList.filter(
    (country) =>
      country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      country.code.includes(searchTerm),
  );

  return (
    <div className={className}>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger className="bg-transparent border-0 rounded-none focus:ring-0 focus:ring-offset-0 px-2 h-auto py-0">
          <div className="flex items-center gap-2 mx-2">
            <span className="text-base">{selectedCountry.flag}</span>
            <span className="text-base">{selectedCountry.code}</span>
          </div>
        </SelectTrigger>
        <SelectContent className="max-h-80">
          <div className="sticky top-0 bg-white p-3 border-b border-gray-100">
            <Input
              type="text"
              placeholder="Search countries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="max-h-60 overflow-auto">
            {filteredCountries.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500">No countries found</div>
            ) : (
              filteredCountries.map((country) => (
                <SelectItem
                  key={`${country.code}-${country.name}`}
                  value={country.name}
                  className="cursor-pointer hover:bg-gray-50 focus:bg-gray-50"
                >
                  <div className="flex items-center gap-3 min-w-0 w-full">
                    <span className="text-lg flex-shrink-0">{country.flag}</span>
                    <span className="flex-1 truncate text-sm font-medium">{country.name}</span>
                    <span className="text-sm text-gray-400 flex-shrink-0 font-mono">
                      {country.code}
                    </span>
                  </div>
                </SelectItem>
              ))
            )}
          </div>
        </SelectContent>
      </Select>
    </div>
  );
};
